// Canvas-rendered 100%-stacked composition bar for the Inventory Surface.
//
// Rendered through RenderMount — the same primitive SurfaceChart uses — so it
// inherits the house resize behavior: observeSettledSize WITHOUT a pin, so the
// previous canvas (styled h-full/w-full) just STRETCHES with the container
// during a resize/expand (a free compositor transform) and exactly one crisp
// redraw lands on settle. (An earlier version copied DotGrid's pin-to-pixels
// recipe, which clips instead of scaling and made the expand pop + flash its
// labels mid-transition.)
//
// Inline band labels are gated on the canvas HEIGHT, not an `expanded` prop:
// they're drawn only once the canvas is tall enough (the settled, expanded
// size), so they appear in the final crisp redraw rather than popping in while
// the bar is still scaling. Segments are opaque on a transparent canvas, so
// the 1px gaps and rounded corners reveal the panel's plot-bg behind. Hover is
// hit-tested from the container width + segments (no canvas dependency, so it
// survives RenderMount's rebuilds) and surfaced via a portaled tooltip.

import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import RenderMount from "./RenderMount.jsx";
import { formatCompact } from "../lib/renderers.js";
import { truncateToWidth } from "../lib/canvasText.js";
import { layoutSegments } from "../lib/compositionLayout.js";
import { TOOLTIP_LAYER } from "../lib/tooltipLayer.js";

const FONT = "Inter, system-ui, sans-serif";
const RADIUS = 2;
const LABEL_MIN_W = 42; // band must be at least this wide to hold a label
const LABEL_MIN_H = 64; // canvas must be at least this tall (i.e. expanded) for labels
// Cap the enumerated members of an aggregate band: a long tail would otherwise
// grow the tooltip past the viewport.
const TOOLTIP_MEMBERS = 8;

// truncateToWidth + layoutSegments live in lib/ (canvasText.js,
// compositionLayout.js) — shared with DotGrid + node-tested.

export default function CompositionBar({ segments = [] }) {
  // RenderMount calls this with the SETTLED size and remounts the canvas; the
  // factory identity changes with `segments` so a data change also redraws.
  const factory = useCallback((size) => {
    const dpr = window.devicePixelRatio || 1;
    const w = size.width;
    const h = size.height;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.save();
    if (ctx.roundRect) {
      ctx.beginPath();
      ctx.roundRect(0, 0, w, h, RADIUS);
      ctx.clip();
    }

    const layout = layoutSegments(segments, w);
    for (const seg of layout) {
      ctx.fillStyle = seg.color;
      ctx.fillRect(seg.x, 0, seg.width, h);
    }

    // Labels only at the settled expanded height. White text + soft shadow
    // stays legible over every band color (incl. the light "unknown" yellow).
    if (h >= LABEL_MIN_H) {
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (const seg of layout) {
        if (seg.width < LABEL_MIN_W) continue;
        const cx = seg.x + seg.width / 2;
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.55)";
        ctx.shadowBlur = 2;
        ctx.shadowOffsetY = 1;
        ctx.fillStyle = "#fff";
        ctx.font = `500 11px ${FONT}`;
        ctx.fillText(truncateToWidth(ctx, seg.label, seg.width - 8), cx, h / 2 - 7);
        ctx.font = `400 10px ${FONT}`;
        ctx.globalAlpha = 0.9;
        ctx.fillText(`${formatCompact(seg.count)} · ${Math.round(seg.share * 100)}%`, cx, h / 2 + 7);
        ctx.restore();
      }
    }
    ctx.restore();
    return canvas;
  }, [segments]);

  // Hover → portaled tooltip, gated on segment transitions (DotGrid's
  // lastHitKey gate) so a move within one band doesn't re-render per frame.
  const [tip, setTip] = useState(null);
  const lastKeyRef = useRef("");
  const onMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const seg = layoutSegments(segments, rect.width).find((s) => x >= s.x && x < s.x + s.width);
    const key = seg ? seg.key : "";
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    if (!seg) { setTip(null); return; }
    const left = Math.min(Math.max(60, rect.left + seg.x + seg.width / 2), window.innerWidth - 60);
    setTip({ left, top: rect.top - 6, seg });
  }, [segments]);
  const onLeave = useCallback(() => {
    if (lastKeyRef.current === "") return;
    lastKeyRef.current = "";
    setTip(null);
  }, []);

  return (
    <div className="relative h-full w-full" onMouseMove={onMove} onMouseLeave={onLeave}>
      <RenderMount factory={factory} className="h-full w-full" />
      {tip ? createPortal(
        <div
          role="tooltip"
          className={`pointer-events-none fixed ${TOOLTIP_LAYER} border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[5px] text-[11px] leading-[1.2] text-[color:var(--ink)] shadow-lg`}
          style={{ left: `${tip.left}px`, top: `${tip.top}px`, transform: "translate(-50%, -100%)" }}
        >
          {/* Header and member rows share one label/value grid: labels start
              at the same x (members indent past the swatch) and values share a
              right edge, so the counts read as a column. */}
          <div className="flex items-center justify-between gap-[12px] whitespace-nowrap">
            <span className="flex items-center gap-[6px]">
              <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: tip.seg.color }} aria-hidden="true" />
              <span className="font-medium">{tip.seg.label}</span>
            </span>
            <span className="shrink-0 tabular-nums text-[color:var(--muted)]">{formatCompact(tip.seg.count)} · {Math.round(tip.seg.share * 100)}%</span>
          </div>
          {/* An aggregate band names what it folded; without this the tail is
              on the bar but still unidentifiable. Rows are nowrap so a long
              provider name widens the tooltip instead of breaking its own
              count across lines. */}
          {tip.seg.members?.length ? (
            <div className="mt-[4px] border-t border-[color:var(--line)] pt-[4px] pl-[13px]">
              {tip.seg.members.slice(0, TOOLTIP_MEMBERS).map((m) => (
                <div key={m.label} className="flex items-center justify-between gap-[12px] whitespace-nowrap">
                  <span className="text-[color:var(--muted)]">{m.label}</span>
                  <span className="shrink-0 tabular-nums text-[color:var(--muted-soft)]">
                    {formatCompact(m.count)} · {Math.round(m.share * 100)}%
                  </span>
                </div>
              ))}
              {tip.seg.members.length > TOOLTIP_MEMBERS ? (
                <div className="whitespace-nowrap text-[color:var(--muted-soft)]">
                  +{tip.seg.members.length - TOOLTIP_MEMBERS} more
                </div>
              ) : null}
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
